// Encrypted export/import (backup & restore) of the whole workspace: every saved website's tab
// data + custom instructions, each site's WP credentials, and the account's API keys.
//
// The backup is optionally encrypted with a user passphrase via the Web Crypto API
// (PBKDF2-SHA256 → AES-GCM-256). The passphrase is never stored — it's required to restore an
// encrypted file. An unencrypted export is allowed but contains WP app passwords + API keys in
// readable form (the UI warns about this).

import { fetchAuditList, fetchAudit, importAuditBlob, saveSiteInstructions, flushNow, GI_TEXT_KEY, GI_ENABLED_KEY } from "./siteData";
import { listSites, getSite, addSite, fetchApiKeys, putApiKeys, type ApiKeys } from "./authClient";

export const BACKUP_APP = "local-seo-garage-backup";
const PBKDF2_ITERATIONS = 200_000;

interface BackupAudit { siteKey: string; label: string; data: Record<string, string> }
interface BackupCred { label: string; siteUrl: string; username: string; appPassword: string }

export interface BackupPayload {
  app: typeof BACKUP_APP;
  version: 1;
  exportedAt: string;
  audits: BackupAudit[];
  credentials: BackupCred[];
  apiKeys: ApiKeys;
}

// The on-disk file: either the plaintext payload (encrypted:false) or an encrypted envelope.
type BackupFile =
  | (BackupPayload & { encrypted: false })
  | { app: typeof BACKUP_APP; version: 1; encrypted: true; kdf: { salt: string; iterations: number }; iv: string; ciphertext: string };

// ── base64 helpers ────────────────────────────────────────────
const b64 = (buf: ArrayBuffer | Uint8Array) => {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
};
const unb64 = (s: string) => Uint8Array.from(atob(s), c => c.charCodeAt(0));

async function deriveKey(passphrase: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey("raw", new TextEncoder().encode(passphrase), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: salt as BufferSource, iterations, hash: "SHA-256" },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

async function encryptPayload(payload: BackupPayload, passphrase: string): Promise<BackupFile> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(passphrase, salt, PBKDF2_ITERATIONS);
  const plaintext = new TextEncoder().encode(JSON.stringify(payload));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, plaintext);
  return { app: BACKUP_APP, version: 1, encrypted: true, kdf: { salt: b64(salt), iterations: PBKDF2_ITERATIONS }, iv: b64(iv), ciphertext: b64(ct) };
}

async function decryptPayload(file: Extract<BackupFile, { encrypted: true }>, passphrase: string): Promise<BackupPayload> {
  const key = await deriveKey(passphrase, unb64(file.kdf.salt), file.kdf.iterations || PBKDF2_ITERATIONS);
  let plain: ArrayBuffer;
  try {
    plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv: unb64(file.iv) }, key, unb64(file.ciphertext));
  } catch {
    throw new Error("Wrong passphrase, or the backup file is corrupted.");
  }
  return JSON.parse(new TextDecoder().decode(plain)) as BackupPayload;
}

// ── Build the backup ──────────────────────────────────────────
export async function buildBackup(passphrase?: string): Promise<{ file: BackupFile; payload: BackupPayload }> {
  await flushNow().catch(() => {}); // persist the active site's unsaved localStorage first

  const { sites } = await fetchAuditList();
  const audits: BackupAudit[] = [];
  for (const s of sites) {
    const blob = await fetchAudit(s.siteKey).catch(() => null);
    if (blob) audits.push({ siteKey: s.siteKey, label: blob.label || s.label, data: blob.data });
  }

  const savedSites = await listSites().catch(() => []);
  const credentials: BackupCred[] = [];
  for (const site of savedSites) {
    const full = await getSite(site.id).catch(() => null);
    if (full) credentials.push({ label: full.label, siteUrl: full.siteUrl, username: full.username, appPassword: full.appPassword });
  }

  const apiKeys = await fetchApiKeys().catch(() => ({ anthropic: "", ahrefs: "", dfsLogin: "", dfsPassword: "" } as ApiKeys));

  const payload: BackupPayload = { app: BACKUP_APP, version: 1, exportedAt: new Date().toISOString(), audits, credentials, apiKeys };
  const file: BackupFile = passphrase ? await encryptPayload(payload, passphrase) : { ...payload, encrypted: false };
  return { file, payload };
}

// Whether a parsed file needs a passphrase before it can be read.
export function backupNeedsPassphrase(fileText: string): boolean {
  try { const f = JSON.parse(fileText) as BackupFile; return f?.app === BACKUP_APP && f.encrypted === true; }
  catch { return false; }
}

// Parse (and decrypt when needed) a backup file's text into a payload.
export async function parseBackup(fileText: string, passphrase?: string): Promise<BackupPayload> {
  let file: BackupFile;
  try { file = JSON.parse(fileText) as BackupFile; }
  catch { throw new Error("This file isn't valid JSON."); }
  if (!file || file.app !== BACKUP_APP) throw new Error("This doesn't look like a Local SEO Garage backup file.");
  if (file.encrypted) {
    if (!passphrase) throw new Error("This backup is encrypted — a passphrase is required.");
    return decryptPayload(file, passphrase);
  }
  return file;
}

// ── Restore ───────────────────────────────────────────────────
export async function restoreBackup(payload: BackupPayload, opts: { credsAndInstructionsOnly: boolean }): Promise<{ sites: number; credentials: number; apiKeys: boolean }> {
  // Credentials → Site Storage (upserts by site_url + username).
  for (const c of payload.credentials) {
    if (!c.siteUrl || !c.username) continue;
    await addSite({ label: c.label, siteUrl: c.siteUrl, username: c.username, appPassword: c.appPassword }).catch(() => {});
  }

  // API keys — restored in BOTH modes (they're account-level secrets, alongside credentials).
  let apiRestored = false;
  if (payload.apiKeys && (payload.apiKeys.anthropic || payload.apiKeys.ahrefs || payload.apiKeys.dfsLogin || payload.apiKeys.dfsPassword)) {
    await putApiKeys(payload.apiKeys).catch(() => {});
    apiRestored = true;
  }

  if (opts.credsAndInstructionsOnly) {
    // Only restore each site's custom (Global) instructions — leave tab data untouched.
    for (const a of payload.audits) {
      const text = a.data[GI_TEXT_KEY] ?? "";
      const enabled = a.data[GI_ENABLED_KEY] === "true";
      if (text || enabled) await saveSiteInstructions(a.siteKey, text, enabled, a.label).catch(() => {});
    }
    return { sites: 0, credentials: payload.credentials.length, apiKeys: apiRestored };
  }

  // Full restore: overwrite each site's whole blob.
  for (const a of payload.audits) {
    await importAuditBlob(a.siteKey, a.data, a.label).catch(() => {});
  }
  return { sites: payload.audits.length, credentials: payload.credentials.length, apiKeys: apiRestored };
}

// Trigger a browser download of a backup file object.
export function downloadBackup(file: BackupFile): void {
  const stamp = new Date().toISOString().slice(0, 10);
  const blob = new Blob([JSON.stringify(file, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `local-seo-garage-backup-${stamp}.json`;
  a.style.display = "none";
  document.body.appendChild(a); // attaching improves cross-browser reliability
  a.click();
  // Clean up on a LATER tick — revoking the blob URL synchronously right after click can cancel
  // the download before the browser has started fetching it (why it silently failed intermittently).
  setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 1500);
}
